/**
 * Walking progress along a route, and how much sun is left.
 *
 * The question while walking at 35 °C is not "how far to go" but *"how much
 * further in the sun?"* — and the router already knows, because shade is stored
 * per edge and per side. Remaining exposure is a partial sum along the route
 * from wherever you are.
 *
 * Everything here is client-side arithmetic over a route already in hand: no
 * request per step, so it updates as fast as the GPS does.
 */

export interface Progress {
  /** Position projected onto the route, `[lng, lat]`. */
  snapped: [number, number]
  /** Metres from the projected point to the destination. */
  remainingM: number
  /** Of those metres, how many are in direct sun. */
  remainingSunM: number
  /** How far along the route, `0..=1`. */
  fraction: number
  /** Metres between the reported position and the route itself. */
  offRouteM: number
}

const EARTH_R = 6_371_008.8

/** Equirectangular metres — exact enough over a city, and much cheaper. */
function metres(a: [number, number], b: [number, number]): number {
  const midLat = ((a[1] + b[1]) / 2) * (Math.PI / 180)
  const x = (b[0] - a[0]) * (Math.PI / 180) * Math.cos(midLat)
  const y = (b[1] - a[1]) * (Math.PI / 180)
  return Math.hypot(x, y) * EARTH_R
}

export class RouteProgress {
  private pts: [number, number][]
  private sun: number[]
  private segLen: number[]
  /** Distance from each vertex to the end of the route. */
  private toEnd: number[]
  /** Sun-metres from each vertex to the end. */
  private sunToEnd: number[]
  readonly totalM: number
  readonly totalSunM: number

  constructor(coordinates: [number, number][], segmentSun: number[]) {
    this.pts = coordinates
    // Defensive: an older API build may omit segmentSun. Treating that as
    // "no data" is better than colouring the whole route as shade.
    this.sun = segmentSun?.length === coordinates.length - 1 ? segmentSun : []

    const n = coordinates.length
    this.segLen = new Array(Math.max(0, n - 1))
    for (let i = 0; i < n - 1; i++) {
      this.segLen[i] = metres(coordinates[i], coordinates[i + 1])
    }

    this.toEnd = new Array(n).fill(0)
    this.sunToEnd = new Array(n).fill(0)
    for (let i = n - 2; i >= 0; i--) {
      this.toEnd[i] = this.toEnd[i + 1] + this.segLen[i]
      this.sunToEnd[i] = this.sunToEnd[i + 1] + this.segLen[i] * (this.sun[i] ?? 0)
    }
    this.totalM = this.toEnd[0] ?? 0
    this.totalSunM = this.sunToEnd[0] ?? 0
  }

  /**
   * Project a position onto the route.
   *
   * A linear scan over every segment: a few hundred of them, so it costs
   * nothing and avoids the bookkeeping of tracking the last match — which
   * would otherwise get stuck when someone doubles back or the GPS jumps.
   */
  locate(position: [number, number]): Progress | null {
    if (this.pts.length < 2) return null

    let bestSeg = 0
    let bestT = 0
    let bestDist = Infinity
    let bestPoint: [number, number] = this.pts[0]

    for (let i = 0; i < this.pts.length - 1; i++) {
      const a = this.pts[i]
      const b = this.pts[i + 1]
      // Work in a local planar frame; over one segment the distortion is nil.
      const midLat = ((a[1] + b[1]) / 2) * (Math.PI / 180)
      const kx = Math.cos(midLat)
      const abx = (b[0] - a[0]) * kx
      const aby = b[1] - a[1]
      const apx = (position[0] - a[0]) * kx
      const apy = position[1] - a[1]
      const len2 = abx * abx + aby * aby
      const t = len2 > 0 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / len2)) : 0
      const px = a[0] + (b[0] - a[0]) * t
      const py = a[1] + (b[1] - a[1]) * t
      const d = metres(position, [px, py])
      if (d < bestDist) {
        bestDist = d
        bestSeg = i
        bestT = t
        bestPoint = [px, py]
      }
    }

    const walkedInSeg = this.segLen[bestSeg] * bestT
    const remainingM = Math.max(0, this.toEnd[bestSeg] - walkedInSeg)
    const remainingSunM = Math.max(
      0,
      this.sunToEnd[bestSeg] - walkedInSeg * (this.sun[bestSeg] ?? 0),
    )

    return {
      snapped: bestPoint,
      remainingM,
      remainingSunM,
      fraction: this.totalM > 0 ? 1 - remainingM / this.totalM : 0,
      offRouteM: bestDist,
    }
  }

  /** Route geometry split at a point, for dimming the part already walked. */
  splitAt(progress: Progress): { walked: [number, number][]; ahead: [number, number][] } {
    const idx = this.indexFor(progress)
    return {
      walked: [...this.pts.slice(0, idx + 1), progress.snapped],
      ahead: [progress.snapped, ...this.pts.slice(idx + 1)],
    }
  }

  private indexFor(progress: Progress): number {
    // Recover the segment from the snapped point rather than storing it, so
    // Progress stays a plain value that can cross a module boundary.
    let best = 0
    let bestDist = Infinity
    for (let i = 0; i < this.pts.length - 1; i++) {
      const d = metres(progress.snapped, this.pts[i])
      if (d < bestDist) {
        bestDist = d
        best = i
      }
    }
    return best
  }
}

export function formatRemaining(p: Progress): string {
  const left = p.remainingM < 1000
    ? `${Math.round(p.remainingM / 10) * 10} m`
    : `${(p.remainingM / 1000).toFixed(1)} km`
  const sun = Math.round(p.remainingSunM / 10) * 10
  if (p.remainingSunM < 15) return `${left} left · all shaded`
  const sunText = p.remainingSunM < 1000 ? `${sun} m` : `${(p.remainingSunM / 1000).toFixed(1)} km`
  return `${left} left · ${sunText} in sun`
}
